import type { AccountService } from '@awork/_shared/services/account-service/account.service'
import { apiEndpoint } from '@awork/environments/environment'
import getHostname from '@awork/_shared/functions/get-hostname'
import { getPortlessWorkspaceOrigin } from '@awork/_shared/functions/portless-hostname'
import { getPixelRatio } from '@awork/_shared/functions/browser-infos'
import type { ImageParams } from '@awork/_shared/services/file-service/types'

export enum ConnectedWorkspaceRole {
  Home = 'home',
  Both = 'both',
  External = 'external',
  Unknown = 'unknown'
}

export interface ConnectedWorkspace {
  id: string
  name: string
  role: ConnectedWorkspaceRole
  subdomains: string[]
}

export enum WorkspaceFeature {
  Planner,
  Scim,
  MocoErpIntegration
}

export enum PreviousTool {
  noSpecificTool = 'noSpecificTool',
  calendarOrExcel = 'excel',
  asana = 'asana',
  monday = 'monday',
  jira = 'jira',
  notion = 'notion'
}

export enum WorkspaceDepartment {
  Marketing = 'marketing',
  HrLegal = 'hr-legal',
  ProductDesign = 'product-design',
  CreativeProduction = 'creative-production',
  Engineering = 'engineering',
  CustomerService = 'customer-service',
  Operations = 'operations',
  Finance = 'finance',
  ItSupport = 'it-support',
  Manufacturing = 'manufacturing',
  SalesAccount = 'sales-account',
  Management = 'management',
  Other = 'other',
  // Agency-specific departments
  ProjectManagement = 'project-management',
  Strategy = 'strategy',
  AdditionalMarketing = 'additional-marketing'
}

export enum WorkspaceIndustry {
  CreativeAgencies = 'creative-agencies',
  Technology = 'technology',
  Consulting = 'consulting',
  RealEstate = 'real-estate',
  NonProfit = 'non-profit',
  Government = 'government',
  ConsumerGoods = 'consumer-goods',
  Healthcare = 'healthcare',
  Education = 'education',
  FinancialServices = 'financial-services',
  Legal = 'legal',
  Media = 'media',
  Private = 'private',
  Other = 'other'
}

export type WorkspaceType = WorkspaceIndustry | string

export interface Subdomain {
  name: string
  isDefault: boolean
}

export interface BetaFeatureSettings {
  show: boolean
}

export interface ConnectSettings {
  disabled: boolean
}

export interface WorkspaceUISetting {
  showTrialExpiredModal: boolean
}

export interface GuestMigrationSetting {
  pending: boolean
}

export interface AIFeaturesSettings {
  disabled: boolean
}

export interface AIAgentsSettings {
  enabledForWorkspace?: boolean
  enabledUserIds?: string[]
  waitlistEnabled?: boolean
}

export interface ProjectKeysSettings {
  enabled: boolean
}

export interface ProjectDeletionConfirmationSettings {
  enabled: boolean
}

export interface WorkspaceSettings {
  teamPlanner?: BetaFeatureSettings
  scimFeature?: BetaFeatureSettings
  mocoErpIntegrationFeature?: BetaFeatureSettings
  basicConnect?: WorkspaceUISetting
  aworkConnect?: ConnectSettings
  guestMigration?: GuestMigrationSetting
  aiFeatures?: AIFeaturesSettings
  aiAgents?: AIAgentsSettings
  projectKeys?: ProjectKeysSettings
  projectDeletionConfirmation?: ProjectDeletionConfirmationSettings
}

interface IWorkspace {
  /** Products available in this workspace, as returned by the API. */
  products?: ('pm' | 'ai')[]
  id?: string
  name: string
  description: string
  image?: string
  hasImage?: boolean
  language: string
  timezone?: string
  lastUsed?: boolean
  lastLogin?: Date | string
  subdomains?: Subdomain[]
  createdOn?: Date
  updatedOn?: Date
  createdBy?: string
  createdByName?: string
  type: WorkspaceType
  department?: WorkspaceDepartment
  size: string
  goals?: string[]
  selfAttribution?: string
  aiUsage?: string
  /** Local onboarding answer for tracking; excluded from workspace API writes. */
  aiLiteracy?: string
  previousTool?: PreviousTool | string
  isApproved?: boolean
  invitationSent?: boolean
  allowCredentialsLogin?: boolean
  isJoinable?: boolean
  isFull?: boolean
  allowSocialLogin?: boolean
  allowSSOLogin?: boolean
  isPaidPlan?: boolean
  settings?: WorkspaceSettings
  isScimActivated?: boolean
  allowAutomaticScimUserBooking?: boolean
  publicImageToken?: string
  sameDomainSignupEnabled?: boolean
  sameDomainSignupDomains?: string[]
  sameDomainSignupRoleId?: string
  requireMfa?: boolean
}

/**
 * The workspace of awork
 */
export class Workspace implements IWorkspace {
  products?: IWorkspace['products']
  id: string
  name: string
  description: string
  image?: string
  hasImage?: boolean
  language: string
  timezone?: string
  lastUsed?: boolean
  lastLogin?: Date | string
  subdomains?: Subdomain[]
  createdOn?: Date
  updatedOn?: Date
  createdBy?: string
  type: WorkspaceType
  department?: WorkspaceDepartment
  size: string
  goals?: string[]
  selfAttribution?: string
  aiUsage?: string
  aiLiteracy?: string
  previousTool?: PreviousTool | string
  isApproved?: boolean
  invitationSent?: boolean
  allowCredentialsLogin?: boolean
  isJoinable?: boolean
  isFull?: boolean
  allowSocialLogin?: boolean
  allowSSOLogin?: boolean
  isPaidPlan?: boolean
  settings?: WorkspaceSettings
  isScimActivated?: boolean
  allowAutomaticScimUserBooking?: boolean
  memberCount: number
  publicImageToken?: string
  sameDomainSignupEnabled?: boolean
  sameDomainSignupDomains?: string[]
  sameDomainSignupRoleId?: string
  requireMfa?: boolean
  createdByName?: string

  constructor(workspace: IWorkspace) {
    Object.assign(this, workspace)

    this.createdOn = new Date(this.createdOn)
  }

  getImage(width = 500, height = 500): string {
    if (this.publicImageToken) {
      return `${apiEndpoint}/files/images/workspaces?${new URLSearchParams({
        token: this.publicImageToken
      }).toString()}`
    }

    const pixelRatio = getPixelRatio()
    const queryOptions: ImageParams = {
      crop: 'false',
      width: Math.floor(width * pixelRatio).toString(),
      height: Math.floor(height * pixelRatio).toString(),
      v: this.hasImage ? new Date(this.updatedOn).getTime().toString() : '1'
    }

    // Set the url
    return `${apiEndpoint}/files/images/workspaces/${this.id}?${new URLSearchParams(queryOptions).toString()}`
  }

  getDefaultSubdomain(accountService?: AccountService): Subdomain {
    if (this.subdomains && this.subdomains.length > 0) {
      const defaultSubdomain = this.subdomains.find(subdomain => subdomain.isDefault)

      return defaultSubdomain ? defaultSubdomain : this.subdomains[0]
    } else {
      // If the workspace does not have subdomain, logout the user (store corrupted or API bad response)
      if (accountService) {
        accountService.logout(null, null, null, { message: `Workspace does not have subdomain` })
      }

      return {
        name: 'www',
        isDefault: false
      }
    }
  }

  /**
   * Returns whether a feature is toggled for this specific workspace
   * @param {WorkspaceFeature} feature
   * @returns {boolean}
   */
  isFeatureEnabled(feature: WorkspaceFeature): boolean {
    // In the future we could have more features to toggle, for this reason we use a switch
    switch (feature) {
      case WorkspaceFeature.Scim:
        return this.settings?.scimFeature?.show
      case WorkspaceFeature.MocoErpIntegration:
        return this.settings?.mocoErpIntegrationFeature?.show ?? false
      default:
        return true
    }
  }

  /**
   * Determines if the current workspace was created before the free plan removal
   * @returns {boolean}
   */
  get isGrandfatheredWorkspace(): boolean {
    // Free plan is removed from 31.01.2020
    const freePlanRemovalDate = new Date(2020, 0, 31)

    return this.createdOn.getTime() < freePlanRemovalDate.getTime()
  }

  /**
   * Get workspace URL
   * E.g: https://custom-subdomain.awork.com
   * @returns {string}
   */
  get workspaceUrl(): string {
    const portlessOrigin = getPortlessWorkspaceOrigin(this.getDefaultSubdomain().name)

    if (portlessOrigin) {
      return portlessOrigin
    }

    const hostname = getHostname()

    return `https://${this.getDefaultSubdomain().name}.${hostname}`
  }
}
